import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import {
  WORK_ORDER_STATUS_MEANINGS,
  PRIORITY_LABELS,
  workOrderStatusLabel,
} from '../../shared/workflow.js';
import type { WorkOrderPriority, WorkOrderStatus } from '../../shared/workflow.js';

export function classes(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-700 text-white hover:bg-brand-800 focus-visible:outline-brand-700',
  secondary:
    'bg-white text-slate-800 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus-visible:outline-slate-500',
  danger: 'bg-red-700 text-white hover:bg-red-800 focus-visible:outline-red-700',
  ghost: 'text-slate-700 hover:bg-slate-100 focus-visible:outline-slate-400',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
}

export function Button({
  variant = 'primary',
  loading = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={classes(
        'inline-flex items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-semibold',
        'transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        className,
      )}
    >
      {loading && (
        <span
          aria-hidden="true"
          className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------

interface FieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
  className?: string;
}

export function Field({ label, htmlFor, required, hint, error, children, className }: FieldProps) {
  return (
    <div className={classes('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-slate-800">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-1 text-red-700">
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-slate-600">{hint}</p>}
      {error && (
        <p role="alert" className="text-xs font-medium text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}

const CONTROL_CLASSES =
  'block w-full rounded-md border-0 px-3 py-2 text-sm text-slate-900 ring-1 ring-inset ring-slate-300 ' +
  'placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-brand-600 disabled:bg-slate-50 ' +
  'disabled:text-slate-500';

export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={classes(CONTROL_CLASSES, className)} />;
}

export function TextArea({
  className,
  rows = 4,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} rows={rows} className={classes(CONTROL_CLASSES, className)} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={classes(CONTROL_CLASSES, 'pr-8', className)}>
      {children}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export function Card({
  title,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={classes('rounded-lg bg-white shadow-sm ring-1 ring-slate-200', className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5">
          {typeof title === 'string' ? (
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          ) : (
            title
          )}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={classes('px-4 py-4 sm:px-5', bodyClassName)}>{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status and priority badges
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<WorkOrderStatus, string> = {
  submitted: 'bg-slate-100 text-slate-800 ring-slate-300',
  under_vgs_review: 'bg-sky-50 text-sky-800 ring-sky-300',
  estimate_in_progress: 'bg-indigo-50 text-indigo-800 ring-indigo-300',
  awaiting_customer_approval: 'bg-amber-50 text-amber-900 ring-amber-300',
  revision_requested: 'bg-orange-50 text-orange-900 ring-orange-300',
  approved: 'bg-emerald-50 text-emerald-800 ring-emerald-300',
  procurement: 'bg-violet-50 text-violet-800 ring-violet-300',
  repair_scheduled: 'bg-teal-50 text-teal-800 ring-teal-300',
  work_in_progress: 'bg-blue-50 text-blue-800 ring-blue-300',
  completed: 'bg-green-50 text-green-800 ring-green-300',
  closed: 'bg-slate-100 text-slate-700 ring-slate-300',
  on_hold: 'bg-yellow-50 text-yellow-900 ring-yellow-300',
  cancelled: 'bg-slate-100 text-slate-600 ring-slate-300',
  rejected: 'bg-red-50 text-red-800 ring-red-300',
};

export function StatusBadge({
  status,
  kind,
}: {
  status: WorkOrderStatus;
  kind?: 'repair' | 'supplies';
}) {
  return (
    <span
      title={WORK_ORDER_STATUS_MEANINGS[status]}
      className={classes(
        'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset',
        STATUS_STYLES[status] ?? 'bg-slate-100 text-slate-800 ring-slate-300',
      )}
    >
      {workOrderStatusLabel(status, kind)}
    </span>
  );
}

const PRIORITY_STYLES: Record<WorkOrderPriority, string> = {
  emergency: 'bg-red-100 text-red-900 ring-red-300',
  urgent: 'bg-orange-100 text-orange-900 ring-orange-300',
  routine: 'bg-slate-100 text-slate-800 ring-slate-300',
  scheduled: 'bg-sky-100 text-sky-900 ring-sky-300',
};

export function PriorityBadge({ priority }: { priority: WorkOrderPriority }) {
  return (
    <span
      className={classes(
        'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset',
        PRIORITY_STYLES[priority],
      )}
    >
      {PRIORITY_LABELS[priority]}
    </span>
  );
}

/**
 * Marks content that must never be shown to a customer user. Rendering is still
 * gated server-side; this is a visual reminder for VGS staff, not the control.
 */
export function InternalBadge({ className }: { className?: string }) {
  return (
    <span
      className={classes(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
        'bg-purple-100 text-purple-900 ring-1 ring-inset ring-purple-300',
        className,
      )}
    >
      VGS internal
    </span>
  );
}

export function InternalPanel({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <div className="rounded-md border border-purple-200 bg-purple-50/60 p-3">
      <div className="mb-2 flex items-center gap-2">
        <InternalBadge />
        {title && <span className="text-sm font-medium text-purple-950">{title}</span>}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export function Alert({
  variant = 'info',
  title,
  children,
  className,
}: {
  variant?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  const styles = {
    info: 'bg-sky-50 text-sky-900 ring-sky-200',
    success: 'bg-emerald-50 text-emerald-900 ring-emerald-200',
    warning: 'bg-amber-50 text-amber-900 ring-amber-200',
    error: 'bg-red-50 text-red-900 ring-red-200',
  }[variant];

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={classes('rounded-md p-3 text-sm ring-1 ring-inset', styles, className)}
    >
      {title && <p className="font-semibold">{title}</p>}
      {children}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <p className="text-sm font-medium text-slate-900">{title}</p>
      {description && <p className="max-w-md text-sm text-slate-600">{description}</p>}
      {action}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-600">
      <span
        aria-hidden="true"
        className="size-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-700"
      />
      <span>{label}</span>
    </div>
  );
}

export function DefinitionList({
  items,
  columns = 2,
}: {
  items: { label: string; value: ReactNode; internal?: boolean }[];
  columns?: 1 | 2 | 3;
}) {
  const gridClass = { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3' }[columns];
  return (
    <dl className={classes('grid grid-cols-1 gap-x-6 gap-y-4', gridClass)}>
      {items.map((item) => (
        <div key={item.label}>
          <dt className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
            {item.label}
            {item.internal && <InternalBadge />}
          </dt>
          <dd className="mt-1 text-sm text-slate-900">
            {item.value || <span className="text-slate-400">—</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}
